/**
 * DeleteSkillDialog — destructive confirmation for a permanent skill
 * delete. Shows the blast radius (versions / active deployments / total
 * blob bytes) and requires the operator to type the skill name to enable
 * the Delete button, matching the legacy dashboard's safety surface.
 *
 * Deleting a skill server-side fans out an uninstall to every endpoint
 * currently carrying it, so a typo-resistant confirm is worth the friction.
 */
import { useCallback, useMemo, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";

const inputCls =
    "min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]";
const dangerBtnCls =
    "h-[30px] rounded-[2px] border border-red-400 bg-red-400/10 px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-red-300 font-medium hover:bg-red-400/20 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer";
const secondaryBtnCls =
    "h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer";

function formatBytes(n) {
    const v = Number(n) || 0;
    if (v < 1024) return `${v} B`;
    if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
    if (v < 1024 * 1024 * 1024) return `${(v / 1024 / 1024).toFixed(2)} MB`;
    return `${(v / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function DeleteSkillDialog({ skill, versions = [], deployments = [], onClose, onConfirm }) {
    const [typed, setTyped] = useState("");
    const [phase, setPhase] = useState("idle"); // idle | deleting
    const [error, setError] = useState(null);

    const totalBytes = useMemo(
        () => versions.reduce((acc, v) => acc + (Number(v.blobSizeBytes ?? v.blob_size_bytes) || 0), 0),
        [versions],
    );
    const versionCount    = versions.length;
    const deploymentCount = deployments.length;
    // Match against either the latest skill name (from GetSkill) or the
    // listing-row name — both should agree but be defensive in case the
    // user opened the dialog before the fetch completed.
    const name = skill?.name ?? "";
    const canDelete = phase === "idle" && typed.trim() === name && !!name;

    const submit = useCallback(async () => {
        if (!canDelete) return;
        setError(null);
        setPhase("deleting");
        try {
            await onConfirm();
            // Parent closes the panel + dialog on success.
        } catch (err) {
            console.error("[skills] delete", err);
            setError(err.message || String(err));
            setPhase("idle");
        }
    }, [canDelete, onConfirm]);

    return (
        <div className="grid gap-[14px]">
            <p className="m-0 text-[12px] leading-[1.5] text-[color:var(--ink)]">
                Deleting a skill is permanent. The skill is removed from every endpoint that
                currently carries it, and the underlying blobs are deleted from object storage.
            </p>

            <div className="grid gap-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px]">
                <div className="flex items-center justify-between gap-[10px] text-[11px]">
                    <span className={NOTE_CLASS}>Skill</span>
                    <span className="text-[color:var(--ink)] font-medium truncate">{name || "—"}</span>
                </div>
                <div className="flex items-center justify-between gap-[10px] text-[11px]">
                    <span className={NOTE_CLASS}>Versions</span>
                    <span className="text-[color:var(--ink)]">{versionCount}</span>
                </div>
                <div className="flex items-center justify-between gap-[10px] text-[11px]">
                    <span className={NOTE_CLASS}>Active deployments</span>
                    <span className={deploymentCount ? "text-amber-300 font-medium" : "text-[color:var(--ink)]"}>
                        {deploymentCount}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-[10px] text-[11px]">
                    <span className={NOTE_CLASS}>Total blob bytes</span>
                    <span className="text-[color:var(--ink)] font-mono">{formatBytes(totalBytes)}</span>
                </div>
            </div>

            <label className="grid gap-[4px]">
                <span className={NOTE_CLASS}>
                    Type the skill name <strong className="text-[color:var(--ink)] font-mono">{name}</strong> to confirm
                </span>
                <input
                    type="text"
                    autoFocus
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder={name}
                    className={inputCls}
                />
            </label>

            {error ? <span className="text-[11px] text-red-400">{error}</span> : null}

            <div className="flex items-center justify-end gap-[6px]">
                <button type="button" onClick={onClose} disabled={phase !== "idle"} className={secondaryBtnCls}>
                    Cancel
                </button>
                <button type="button" onClick={submit} disabled={!canDelete} className={dangerBtnCls}>
                    {phase === "deleting" ? "Deleting…" : "Delete permanently"}
                </button>
            </div>
        </div>
    );
}
